import { useClickOutSide } from "@/components/hooks/useClickOutSide";
import { color } from "@/styles/theme";
import {
  DropDownMenu,
  DropDownMenuItem,
  DropDownMenuSeparator,
  Font,
  Icon,
  ICONS,
} from "@moneyforward/mfx_biz_react_components";

import { Button } from "react-aria-components";

import { useRouter } from "next/router";
import { urlWithQuery } from "@/libs/url";
import {
  createContext,
  Dispatch,
  forwardRef,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react";

const GlobalHeaderDropDownContext = createContext<{
  showDropDown: boolean;
  setShowDropDown: Dispatch<SetStateAction<boolean>>;
}>({
  showDropDown: false,
  setShowDropDown: () => {},
});

export const GlobalHeaderDropDown = ({ children }: { children: ReactNode }) => {
  const [showDropDown, setShowDropDown] = useState(false);

  const node = useClickOutSide((e) => setShowDropDown(false));

  return (
    <GlobalHeaderDropDownContext.Provider
      value={{ showDropDown, setShowDropDown }}
    >
      <div ref={node}>{children}</div>
    </GlobalHeaderDropDownContext.Provider>
  );
};

export const GlobalHeaderDropDownHeader = ({
  icon,
  children,
}: {
  icon: keyof typeof ICONS;
  children?: ReactNode;
}) => {
  const { showDropDown, setShowDropDown } = useContext(
    GlobalHeaderDropDownContext
  );
  return (
    <GlobalHeaderDropDownButton
      onPress={() => {
        setShowDropDown(!showDropDown);
      }}
      icon={icon}
    >
      {children}
    </GlobalHeaderDropDownButton>
  );
};

interface GlobalHeaderDropDownButtonProps {
  icon: keyof typeof ICONS;
  children?: ReactNode;
  onPress?: () => void;
}

// We re-implement Header.HeaderButton so that we have more control whether <Font> is rendered or not.
//
// This is necessary as in spMode:
// - we only want an icon to be displayed;
// - the presence of <Font> causes a <p> to be rendered, even if children is empty;
// - bcause of `gap: 8`, the icon is shifted 8 pixels to the left.
//
// We have asked engineers maintaining mfx_biz_react_components to consider changing <HeaderButton>
// to allow users like us more control.
// (c.f. https://github.com/moneyforward/mfx_biz_react_components/issues/284)
//
// If they agree to our request, we can go back to using <HeaderButton>.
// If not, we need to continue using this workaround.
export const GlobalHeaderDropDownButton = forwardRef<
  HTMLButtonElement,
  GlobalHeaderDropDownButtonProps
>(({ icon, children, ...rest }, ref) => {
  return (
    <Button
      css={{
        padding: "4px 16px",
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        gap: 8,
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: color.gray100,
          cursor: "pointer",
        },
      }}
      ref={ref}
      {...rest}
    >
      <Icon size="md" name={icon} />
      {/*other than the following line, this component is otherwise identical to HeaderButton*/}
      {children && <Font>{children}</Font>}
    </Button>
  );
});

GlobalHeaderDropDownButton.displayName = "HeaderButton";

export const GlobalHeaderDropDownBody = ({
  width,
  children,
}: {
  width: number;
  children: ReactNode;
}) => {
  const { showDropDown } = useContext(GlobalHeaderDropDownContext);
  return (
    <div
      css={{
        position: "relative",
        display: showDropDown ? "block" : "none",
        zIndex: 1,
      }}
    >
      <div
        css={{
          position: "absolute",
          width: width,
          top: 4,
          right: 0,
          backgroundColor: color.white,
        }}
      >
        <DropDownMenu>{children}</DropDownMenu>
      </div>
    </div>
  );
};

export const GlobalHeaderDropDownItem = ({
  height = 40,
  children,
  ...rest
}: {
  height?: number;
  children: ReactNode;
}) => {
  return (
    <DropDownMenuItem height={height} {...rest}>
      {children}
    </DropDownMenuItem>
  );
};

export const GlobalHeaderDropDownItemLink = ({
  href,
  openInNewTab = false,
  children,
  icon,
}: {
  href: string;
  openInNewTab?: boolean;
  children: ReactNode;
  icon?: keyof typeof ICONS;
}) => {
  const target = openInNewTab ? "_blank" : undefined;
  const rel = openInNewTab ? "noopener noreferrer" : undefined;

  return (
    <a href={href} target={target} rel={rel}>
      <GlobalHeaderDropDownItem
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: "row",
        }}
      >
        {children}
        {icon && <Icon name={icon} />}
      </GlobalHeaderDropDownItem>
    </a>
  );
};

export const GlobalHeaderDropDownItemSignOut = () => {
  const router = useRouter();

  const signOutURL = urlWithQuery("/sign-out", router.query.app);

  return (
    <GlobalHeaderDropDown.ItemLink href={signOutURL}>
      ログアウト
    </GlobalHeaderDropDown.ItemLink>
  );
};

GlobalHeaderDropDown.Header = GlobalHeaderDropDownHeader;
GlobalHeaderDropDown.Body = GlobalHeaderDropDownBody;
GlobalHeaderDropDown.Item = GlobalHeaderDropDownItem;
GlobalHeaderDropDown.ItemLink = GlobalHeaderDropDownItemLink;
GlobalHeaderDropDown.ItemSignOut = GlobalHeaderDropDownItemSignOut;
GlobalHeaderDropDown.Separator = DropDownMenuSeparator;
